<template scoped>
  <panel class="case-dispatch" :title="'Dispatch Report' | translateFeatureLabel" color="blue-5">
    <div class="dispatch-fields">
      <div class="dispatch-left">
        <div class="dispatch-field-container">
          <div class="dispatch-field-label-container" data-testid="scenario-information-container">
            <label for="dispatch-information">Scenario Information
              <md-tooltip md-direction="right" data-testid="scenario-information-hover-tooltip">
                Information the User will see when starting this scenario.
              </md-tooltip>
            </label>
          </div>
          <html-editor
            aria-label="Scenario Information"
            data-testid="scenario-information-editor"
            :contents="getFromDispatch('chiefComplaint')"
            :readonly="readonly"
            :max-chars="3000"
            height="100px"
            @update="contents => updateDispatchHtml('chiefComplaint', contents)"
          />
        </div>
        <div class="dispatch-field-container" data-testid="goal-container">
          <div class="dispatch-field-label-container">
            <label for="goal">
              {{ 'Goal' | translateFeatureLabel }}
              <md-tooltip md-direction="right" data-testid="goal-hover-tooltip">
                Used for internal information and purpose of a scenario.
              </md-tooltip>
            </label>
            <md-checkbox
              class="show-on-load-screen-checkbox md-primary"
              :value="isDispatchFieldDisplayed('goal')"
              :disabled="readonly"
              aria-label="Show on Load Screen"
              data-testid="show-on-load-screen-checkbox"
              @change="value => updateDispatchFieldDisplayed('goal', value)"
            >
              Show on Load Screen
            </md-checkbox>
          </div>
          <html-editor
            aria-label="Goal"
            data-testid="goal-editor"
            :contents="getFromDispatch('goal')"
            :readonly="readonly"
            :max-chars="3000"
            height="100px"
            @update="contents => updateDispatchHtml('goal', contents)"
          />
        </div>
        <div class="dispatch-field-container" data-testid="learning-objectives-container">
          <div class="dispatch-field-label-container">
            <label for="learning-objectives">
              Learning Objectives
            </label>
            <help-tooltip md-direction="right" data-testid="learning-objectives-tooltip">
              Learning Objectives display at scenario load only
            </help-tooltip>
          </div>
          <md-field :md-counter="true">
            <multiline-textarea
              input-id="learning-objectives"
              :value="getFromDispatch('learningObjectives')"
              :disabled="readonly"
              :number-of-lines="5"
              :maxlength="300"
              aria-label="Learning Objectives"
              data-testid="learning-objectives-multiline-text-area"
              @input="learningObjectives => updateDispatch('learningObjectives', learningObjectives)"
            />
          </md-field>
        </div>

        <div class="dispatch-field-container" data-testid="scenario-path-container">
          <html-editor
            label="Scenario Path"
            aria-label="Scenario Path"
            data-testid="scenario-path-editor"
            :contents="caseConf.dispatch.scenarioScript"
            :readonly="readonly"
            height="100px"
            @update="updateScenarioScript"
          />
        </div>
      </div>
      <div class="dispatch-right">
        <div class="dispatch-field-container" data-testid="tags-container">
          <div class="dispatch-field-label-container">
            <label for="tags-input">Tags
              <md-tooltip md-direction="right" data-testid="tags-hover-tooltip">
                Used for sorting on the scenario select screen
              </md-tooltip>
            </label>
          </div>
          <md-field class="tags-field md-scrollbar" @click.native="focusNewTagInput">
            <md-chip
              v-for="(tag, index) in caseConf.tags"
              :key="index"
              class="md-primary tag"
              aria-label="Selected Tag"
              data-testid="selected-tag"
              :md-deletable="!readonly"
              @md-delete="removeTag(index)"
            >
              {{ tag.text }}
            </md-chip>
            <autocomplete
              v-if="!readonly"
              ref="newTagInput"
              input-id="tags-input"
              aria-label="New Tag"
              data-testid="new-tag-input"
              :value="state.newTagText"
              :results="knownTagsResults"
              :maxlength="30"
              :placeholder="state.tagsInputFocused ? 'Enter custom tag...' : '+ Add Tag'"
              :show-add-button="false"
              :show-search-icon="false"
              @input="text => updateNewTagText(text)"
              @selectResult="result => addNewTag(result.label, true)"
              @focus="onTagInputFocus"
              @blur="onTagInputBlur"
              @keydown.native.enter="addNewTag()"
            />
          </md-field>
          <span
            v-for="(tagText) in suggestedTags"
            :key="tagText"
          >
            <md-chip
              v-if="!tagTexts.includes(tagText)"
              :md-deletable="false"
              class="suggested-tag"
              :aria-label="`Suggested Tag: ${tagText}`"
              data-testid="suggested-tag"
            >
              {{ tagText }}
              <md-button class="md-icon-button md-dense" aria-label="Add Tag" data-testid="add-tag-button" @click="addNewTag(tagText)">
                <md-icon>add</md-icon>
              </md-button>
            </md-chip>
          </span>
          <div v-if="isCodeStatusAvailable()" class="code-status-certification-container">
            <patient-code-status
              :case-conf="caseConf"
              aria-label="Code Status"
              data-testid="code-status"
              @update="update"
            />
          </div>
          <div v-if="isCertificationLevelsAvailable()" class="code-status-certification-container" data-testid="certification-container">
            <case-certification-levels
              :case-conf="caseConf"
              aria-label="Certification Levels"
              data-testid="certification-levels"
              @update="update"
            />
          </div>
        </div>
      </div>
    </div>
  </panel>
</template>

<script>
import Fuse from 'fuse.js';
import Autocomplete from '~lib/common/components/Autocomplete';
import HelpTooltip from '~lib/common/components/HelpTooltip.vue';
import Panel from '../../common/Panel';
import MultilineTextarea from '../../common/MultilineTextarea.vue';
import suggestedTags from './suggestedTags.json';
import knownTags from './knownTags.json';
import PatientCodeStatus from '../PatientCodeStatus';
import CaseCertificationLevels from '../CaseCertificationLevels';
import HtmlEditor from '~lib/common/components/HtmlEditor/HtmlEditor.vue';

export default {
  name: 'case-dispatch',
  components: {
    Panel,
    Autocomplete,
    PatientCodeStatus,
    CaseCertificationLevels,
    MultilineTextarea,
    HelpTooltip,
    HtmlEditor,
  },
  props: {
    caseConf: {
      type: Object,
      required: true,
    },
    readonly: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      state: {
        newTagText: '',
        tagsInputFocused: false,
      },
    };
  },
  computed: {
    suggestedTags() {
      let tags = suggestedTags['COMMON'];
      if (this.$featureAvailabilityService.productType() === 'NURSING') {
        tags = tags.concat(suggestedTags['NURSING'])
      }
      return tags;
    },
    knownTags() {
      let tags = knownTags['COMMON'];
      if (this.$featureAvailabilityService.productType() === 'NURSING') {
        tags = tags.concat(knownTags['NURSING'])
      }
      return tags;
    },
    tagTexts() {
      return (this.caseConf.tags || []).map(tag => tag.text);
    },
    knownTagsResults() {
      const { newTagText } = this.state;
      if (!newTagText) {
        return this.knownTags
          .map(tagText => ({
            id: tagText,
            label: tagText,
          }))
      }
      // eslint-disable-next-line vue/no-side-effects-in-computed-properties
      this.fuse = this.fuse ||
        new Fuse(this.knownTags, {
          distance: 200,
          threshold: 0.3,
          shouldSort: true,
        });

      const filteredTags = this.fuse.search(newTagText).map(index => this.knownTags[index]);
      return filteredTags
        .map(tagText => ({
          id: tagText,
          label: tagText,
        }));
    },
  },
  watch: {
    // When the tags change, the input shifts
    // so we need to reposition the autocomplete
    'caseConf.tags'() {
      this.$nextTick(() => {
        this.repositionAutocomplete();
        // In case a tag was deleted, we need to wait
        // for the chip remove animation before repositioning
        setTimeout(() => {
          this.repositionAutocomplete();
        }, 400);
      });
    },
  },
  methods: {
    updateDispatch(key, text) {
      this.$emit('update', {
        dispatch: {
          [key]: {
            caption: {
              en: text,
            },
          },
        },
      });
    },
    getFromDispatch(key) {
      const { dispatch } = this.caseConf;
      if (!dispatch) {
        return '';
      }
      if (!dispatch[key]) {
        return '';
      }
      return dispatch[key].caption.en;
    },
    isDispatchFieldDisplayed(key) {
      const { dispatch } = this.caseConf;
      if (!dispatch) {
        return false;
      }
      if (!dispatch[key]) {
        return false;
      }
      return dispatch[key].displayed;
    },
    updateDispatchFieldDisplayed(key, value) {
      this.caseConf.dispatch = this.caseConf.dispatch || {};
      this.caseConf.dispatch[key] = this.caseConf.dispatch[key] || {}
      this.caseConf.dispatch[key].displayed = value;
      this.$emit('update', {
        dispatch: this.caseConf.dispatch,
      });
    },
    addNewTag(newTagText = this.state.newTagText, isFromMenu = false) {
      if (!isFromMenu) {
        const highlightedMenuItem = document.querySelector('.md-menu-item.md-highlight');
        if (highlightedMenuItem) {
          return;
        }
      }
      if (!newTagText) {
        return;
      }
      this.$emit('update', {
        tags: (this.caseConf.tags || []).concat({ text: newTagText }),
      });
      this.state.newTagText = '';
      this.$analytics.event('Authoring Tool', 'enter_tag', newTagText);
    },
    updateNewTagText(text) {
      this.state.newTagText = text;
    },
    onTagInputFocus() {
      this.state.tagsInputFocused = true;
    },
    onTagInputBlur() {
      this.state.tagsInputFocused = false;
    },
    removeTag(indexToDelete) {
      this.$emit('update', {
        tags: this.caseConf.tags.filter((tag, tagIndex) => tagIndex !== indexToDelete),
      });
    },
    focusNewTagInput() {
      this.$refs.newTagInput.$el.focus();
    },
    repositionAutocomplete() {
      if (this.$refs.newTagInput) {
        this.$refs.newTagInput.repositionMenu();
      }
    },
    isCodeStatusAvailable() {
      return this.$featureAvailabilityService.isAvailable('DISPATCH_CODE_STATUS');
    },
    isCertificationLevelsAvailable() {
      return this.$featureAvailabilityService.isAvailable('DISPATCH_CERTIFICATION_LEVEL');
    },
    updateDispatchHtml(key, contents) {
      this.$emit('update', {
        dispatch: {
          [key]: {
            caption: {
              en: this.stripListItemParagraphs(contents.markup),
            },
          },
        },
      });
    },
    stripListItemParagraphs(html) {
      const doc = new DOMParser().parseFromString(html, 'text/html');
      doc.querySelectorAll('li > p').forEach(p => {
        while (p.firstChild) p.parentNode.insertBefore(p.firstChild, p);
        p.remove();
      });
      return doc.body.innerHTML;
    },
    updateScenarioScript(contents) {
      this.$emit('update', {
        dispatch: {
          scenarioScript: contents.markup
        }
      });
    },
    update(updates) {
      this.$emit('update', updates);
    },
  },
};
</script>

<style scoped>
.dispatch-fields {
  display: flex;
}

.dispatch-left {
  display: flex;
  flex-direction: column;
  width: 66.6%;
}

.dispatch-right {
  display: flex;
  flex-direction: column;
  width: 33.3%;
  padding-left: 45px;
}

.dispatch-field-container {
  margin-top: 45px;
}

.dispatch-field-container .md-textarea {
  margin-right: 0;
}

.dispatch-field-container .md-textarea {
  /* resize: none !important; */
  height: auto;
  max-height: none;
}

.dispatch-field-label-container {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  padding-bottom: 8px;
}

.dispatch-field-container .md-checkbox {
  margin: 0;
  margin-left: 8px;
}

.show-on-load-screen-checkbox {
  display: flex;
  flex-direction: row-reverse;
}

.show-on-load-screen-checkbox >>> .md-checkbox-label {
  padding-right: 16px;
}

.tags-field {
  border-radius: 5px;
  margin-bottom: 16px;
  display: flex;
  flex-wrap: wrap;
}

.tags-field .autocomplete {
  width: 100%;
}

.tags-field .autocomplete >>> input {
  font-size: 16px;
}

.tags-field .autocomplete >>> input::placeholder {
  font-size: 16px;
}

.md-chip.tag {
  margin-right: 5px;
  font-size: 16px;
  margin-top: 5px;
  margin-bottom: 5px;
}

.md-chip.tag, .md-chip.tag:hover {
  background-color: #0f5398 !important;
}

/* Display ellipsis if the tag exceeds the field width */
.md-chip.tag {
  display: inline-block;
  max-width: 100%;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}

.suggested-tag {
  background-color: #e0e0e0;
  font-size: 16px;
  margin-right: 5px;
  margin-top: 5px;
  margin-bottom: 5px;
}

.suggested-tag .md-button {
  color: black;
}

.code-status-certification-container {
  margin-top: 25px;
}
</style>

<style>
.case-dispatch .md-chip.tag:hover .md-icon svg {
  fill: #fff !important;
  color: #fff !important;
}
</style>
